/**
 * CandidateAuthContext - Separate auth context for the candidate portal.
 * Stores candidate JWT in localStorage under 'lenvia_candidate_token'.
 * Completely separate from the admin AuthContext.
 */
import { createContext, useContext, useState, useCallback } from "react";
import apiClient from "@/lib/api";

const CandidateAuthContext = createContext(null);

const CANDIDATE_TOKEN_KEY = "lenvia_candidate_token";
const CANDIDATE_ID_KEY = "lenvia_candidate_id";

export const CandidateAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(CANDIDATE_TOKEN_KEY));
  const [candidateId, setCandidateId] = useState(() => localStorage.getItem(CANDIDATE_ID_KEY));
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const requestOtp = useCallback(async (email) => {
    setError(null);
    setLoading(true);
    try {
      await apiClient.post("/candidate-auth/request-otp", { email });
      return { ok: true };
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send OTP";
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtp = useCallback(async (email, otp) => {
    setError(null);
    setLoading(true);
    try {
      const res = await apiClient.post("/candidate-auth/verify-otp", { email, otp });
      const { access_token, candidate_id } = res.data;
      setToken(access_token);
      setCandidateId(candidate_id);
      localStorage.setItem(CANDIDATE_TOKEN_KEY, access_token);
      localStorage.setItem(CANDIDATE_ID_KEY, candidate_id);
      return { ok: true, candidate_id };
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid or expired OTP";
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setCandidateId(null);
    localStorage.removeItem(CANDIDATE_TOKEN_KEY);
    localStorage.removeItem(CANDIDATE_ID_KEY);
  }, []);

  /**
   * Make an authenticated call using the candidate token.
   * The consumer is responsible for calling this instead of the admin apiClient.
   */
  const candidateApi = useCallback(
    (config) => {
      return apiClient({
        ...config,
        headers: {
          ...(config.headers || {}),
          Authorization: `Bearer ${token}`,
        },
      });
    },
    [token],
  );

  return (
    <CandidateAuthContext.Provider
      value={{
        token,
        candidateId,
        isAuthenticated: !!token,
        loading,
        error,
        requestOtp,
        verifyOtp,
        logout,
        candidateApi,
      }}
    >
      {children}
    </CandidateAuthContext.Provider>
  );
};

export const useCandidateAuth = () => {
  const ctx = useContext(CandidateAuthContext);
  if (!ctx) throw new Error("useCandidateAuth must be inside CandidateAuthProvider");
  return ctx;
};

export default CandidateAuthContext;
